/** Disabled-by-default tenant conversion policies, hosted in Administration. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "@/api/client";
import { errorMessage, unwrap } from "@/api/problem";
import { EntityPicker } from "@/shared/components/pickers";
import { FormDialog } from "@/shared/components/form-dialog";
import { EmptyState, ErrorState, LoadingState } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";
import { Field, Input, Select, Checkbox } from "@/shared/ui/form";
import { Table, Th, Td, Tr } from "@/shared/ui/primitives";
import { REQUEST_CATEGORIES, REQUEST_SEVERITIES } from "./queries";

/** Configure only category/severity/site conditions and a template/team result. */
export function AutoConversionRules(): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Schemas["AutoRuleOut"] | "new" | null>(null);
  const rules = useQuery({
    queryKey: ["requests", "auto-rules"],
    queryFn: async () => unwrap(await api.GET("/api/v1/requests/auto-conversion-rules")),
  });
  const save = useMutation({
    mutationFn: async ({ id, body }: { id?: string; body: Schemas["AutoRuleIn"] }) =>
      id
        ? unwrap(
            await api.PUT("/api/v1/requests/auto-conversion-rules/{rule_id}", {
              params: { path: { rule_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/requests/auto-conversion-rules", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests", "auto-rules"] });
      setEditing(null);
    },
  });
  return (
    <section className="my-4 space-y-2">
      <Button variant="outline" onClick={() => setEditing("new")}>
        {t("requests.newAutomationRule")}
      </Button>
      {rules.isPending ? (
        <LoadingState />
      ) : rules.isError ? (
        <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
      ) : rules.data.length === 0 ? (
        <EmptyState message={t("requests.noAutomationRules")} />
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>{t("app.name")}</Th>
              <Th>{t("requests.ruleConditions")}</Th>
              <Th>{t("requests.ruleOutcome")}</Th>
              <Th>{t("app.status")}</Th>
              <Th>{t("app.actions")}</Th>
            </Tr>
          </thead>
          <tbody>
            {rules.data.map((rule) => (
              <Tr key={rule.id}>
                <Td>{rule.name}</Td>
                <Td>
                  {t(`requests.category.${rule.category}`)} ·{" "}
                  {rule.severity ? t(`requests.severity.${rule.severity}`) : t("app.all")} ·{" "}
                  {rule.site_name ?? t("requests.allSites")}
                </Td>
                <Td>
                  {t(`workOrders.type.${rule.work_order_type}`)}
                  <br />
                  {rule.assigned_team_name ?? t("workOrders.unassigned")} ·{" "}
                  {rule.job_template_name ?? t("requests.noJobTemplate")}
                </Td>
                <Td>{rule.enabled ? t("requests.ruleEnabled") : t("requests.ruleDisabled")}</Td>
                <Td>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`${t("app.edit")}: ${rule.name}`}
                    onClick={() => setEditing(rule)}
                  >
                    {t("app.edit")}
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing ? (
        <RuleDialog
          key={editing === "new" ? "new" : editing.id}
          rule={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          pending={save.isPending}
          error={save.isError ? errorMessage(save.error) : undefined}
          onSave={(body) =>
            save.mutateAsync({ id: editing === "new" ? undefined : editing.id, body })
          }
        />
      ) : null}
    </section>
  );
}

function RuleDialog({
  rule,
  onClose,
  onSave,
  pending,
  error,
}: {
  rule?: Schemas["AutoRuleOut"];
  onClose: () => void;
  onSave: (body: Schemas["AutoRuleIn"]) => Promise<unknown>;
  pending: boolean;
  error?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState(rule?.name ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? false);
  const [severity, setSeverity] = useState(rule?.severity ?? "");
  const [category, setCategory] = useState(rule?.category ?? "fault");
  const [site, setSite] = useState(rule?.site_id ?? "");
  const [team, setTeam] = useState(rule?.assigned_team_id ?? "");
  const [template, setTemplate] = useState(rule?.job_template_id ?? "");
  const [type, setType] = useState(rule?.work_order_type ?? "inspection");
  return (
    <FormDialog
      open
      title={t("requests.automaticConversion")}
      onOpenChange={(open) => !open && onClose()}
      submitting={pending}
      error={error}
      disabled={!name.trim()}
      onSubmit={() =>
        onSave({
          name,
          enabled,
          severity,
          category,
          site_id: site || null,
          assigned_team_id: team || null,
          job_template_id: template || null,
          work_order_type: type,
        })
      }
    >
      <p className="text-sm">{t("requests.ruleExplanation")}</p>
      <Field id="rule-name" label={t("app.name")}>
        <Input id="rule-name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Checkbox
        id="rule-enabled"
        label={t("requests.ruleEnabled")}
        checked={enabled}
        onChange={(event) => setEnabled(event.target.checked)}
      />
      <Field id="rule-category" label={t("app.type")}>
        <Select
          id="rule-category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          {REQUEST_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`requests.category.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="rule-severity" label={t("app.severity")}>
        <Select
          id="rule-severity"
          value={severity}
          onChange={(event) => setSeverity(event.target.value)}
        >
          <option value="">{t("app.all")}</option>
          {REQUEST_SEVERITIES.map((value) => (
            <option key={value} value={value}>
              {t(`requests.severity.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="rule-site" label={t("app.site")}>
        <EntityPicker
          id="rule-site"
          value={site}
          onChange={(value) => setSite(value ?? "")}
          queryKey={["requests", "automation-options", "sites"]}
          searchable
          load={async (q) =>
            unwrap(
              await api.GET("/api/v1/requests/auto-conversion-options", {
                params: { query: { kind: "site", q } },
              }),
            ).map((row) => ({
              id: row.id,
              label: row.name,
            }))
          }
        />
      </Field>
      <Field id="rule-team" label={t("workOrders.team")}>
        <EntityPicker
          id="rule-team"
          value={team}
          onChange={(value) => setTeam(value ?? "")}
          queryKey={["requests", "automation-options", "teams"]}
          searchable
          load={async (q) =>
            unwrap(
              await api.GET("/api/v1/requests/auto-conversion-options", {
                params: { query: { kind: "team", q } },
              }),
            ).map((row) => ({
              id: row.id,
              label: row.name,
            }))
          }
        />
      </Field>
      <Field id="rule-template" label={t("requests.jobTemplate")}>
        <EntityPicker
          id="rule-template"
          value={template}
          onChange={(value) => setTemplate(value ?? "")}
          queryKey={["requests", "automation-options", "templates"]}
          searchable
          load={async (q) =>
            unwrap(
              await api.GET("/api/v1/requests/auto-conversion-options", {
                params: { query: { kind: "template", q } },
              }),
            ).map((row) => ({
              id: row.id,
              label: row.name,
            }))
          }
        />
      </Field>
      <Field id="rule-type" label={t("requests.conversionType")}>
        <Select id="rule-type" value={type} onChange={(event) => setType(event.target.value)}>
          <option value="inspection">{t("workOrders.type.inspection")}</option>
          <option value="corrective">{t("workOrders.type.corrective")}</option>
        </Select>
      </Field>
    </FormDialog>
  );
}
